import { Component } from '@theme/component';
import { debounce, isClickedOutside, onAnimationEnd } from '@theme/utilities';

/**
 * @param {HTMLDialogElement} current
 * @returns {boolean}
 */
function hasOtherOpenDialog(current) {
  for (const dialog of document.querySelectorAll('dialog[open]')) {
    if (!(dialog instanceof HTMLDialogElement) || dialog === current) continue;
    if (dialog.hasAttribute('data-qa-non-modal')) continue;
    return true;
  }

  return false;
}

/**
 * @param {HTMLElement} host
 * @param {HTMLDialogElement} dialog
 */
function syncNestedDrawerChrome(host, dialog) {
  if (host.hasAttribute('data-drawer-root')) return;

  const nested = hasOtherOpenDialog(dialog);
  host.toggleAttribute('data-drawer-nested', nested);

  if (!nested) return;

  const titlebar = host.querySelector('.dev-drawer__titlebar');
  if (!(titlebar instanceof HTMLElement)) return;
  if (titlebar.querySelector('[data-dev-drawer-back]')) return;

  const template = document.querySelector('[data-dev-drawer-back-template]');
  if (!(template instanceof HTMLTemplateElement)) return;

  const source = template.content.querySelector('[data-dev-drawer-back]');
  if (!(source instanceof HTMLElement)) return;

  titlebar.prepend(source.cloneNode(true));
}

/**
 * A custom element that manages a dialog.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener('resize', this.#handleResize);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.minWidth || this.maxWidth) {
      window.removeEventListener('resize', this.#handleResize);
    }
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) return;

    const windowWidth = window.innerWidth;
    if (windowWidth < minWidth || windowWidth > maxWidth) {
      this.closeDialog();
    }
  }, 50);

  #previousScrollY = 0;

  /** Whether this instance applied body scroll-lock (false when nested under another open dialog). */
  #ownsScrollLock = false;

  /**
   * Take over an existing body scroll-lock (e.g. cart opens while quick-add still owns the lock).
   * Without this, quick-add closes without unlocking and cart never owned the lock → permanent freeze.
   * @param {number} scrollY
   */
  adoptScrollLock(scrollY) {
    this.#ownsScrollLock = true;
    this.#previousScrollY = scrollY;
    document.body.style.width = '100%';
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
  }

  #unlockBodyScroll() {
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';
    window.scrollTo({ top: this.#previousScrollY, behavior: 'instant' });
  }

  /**
   * Release or transfer body scroll-lock after the dialog has closed.
   * Shared by closeDialog() and native dialog.close() (variant drawer selection).
   */
  #releaseScrollLockAfterClose() {
    if (!this.#ownsScrollLock) return;

    // After close(), our dialog is no longer [open]. If another *modal* dialog is still
    // open (typical: cart drawer after quick-add ATC), transfer lock ownership so the
    // remaining dialog can unlock on its own close — otherwise body stays position:fixed.
    //
    // Only hand off to top-layer modals (`dialog.showModal()` → `:modal`). Non-modal
    // panels (quick-add size drawer via `dialog.show()`) must never inherit the lock —
    // they never run showDialog()/unlock and would freeze the page.
    const stillOpen = this.#findOpenModalDialog();
    if (!stillOpen) {
      this.#unlockBodyScroll();
    } else {
      const nextHost =
        stillOpen.parentElement instanceof DialogComponent
          ? stillOpen.parentElement
          : stillOpen.closest('cart-drawer-component, quick-add-dialog, dialog-component');
      if (nextHost instanceof DialogComponent && nextHost !== this) {
        nextHost.adoptScrollLock(this.#previousScrollY);
      } else {
        // Prefer unfreezing over leaving the page stuck.
        this.#unlockBodyScroll();
      }
    }

    this.#ownsScrollLock = false;
  }

  /**
   * Whether `:modal` can be used (cached). Unsupported engines use a structural fallback.
   * @type {boolean | null}
   */
  static #supportsModalPseudo = null;

  /**
   * Top-layer modal opened with showModal(), not a nested non-modal panel (dialog.show()).
   * @param {HTMLDialogElement} dialog
   * @returns {boolean}
   */
  #dialogIsTopLayerModal(dialog) {
    if (DialogComponent.#supportsModalPseudo === null) {
      try {
        document.createElement('dialog').matches(':modal');
        DialogComponent.#supportsModalPseudo = true;
      } catch {
        DialogComponent.#supportsModalPseudo = false;
      }
    }

    if (DialogComponent.#supportsModalPseudo) {
      return dialog.matches(':modal');
    }

    // Fallback without :modal — never hand off to quick-add ported size panels.
    if (
      dialog.closest('[data-qa-drawer-ported]') ||
      dialog.hasAttribute('data-qa-non-modal') ||
      dialog.closest('[data-quick-add-variant-drawer]')
    ) {
      return false;
    }

    // Nested open dialog inside another open dialog is not the lock owner.
    let parent = dialog.parentElement;
    while (parent) {
      if (parent instanceof HTMLDialogElement && parent.open) return false;
      parent = parent.parentElement;
    }

    return true;
  }

  /**
   * @returns {HTMLDialogElement | null}
   */
  #findOpenModalDialog() {
    for (const dialog of document.querySelectorAll('dialog[open]')) {
      if (!(dialog instanceof HTMLDialogElement)) continue;
      if (this.#dialogIsTopLayerModal(dialog)) return dialog;
    }
    return null;
  }

  /**
   * Dialogs keep their overflow scrollTop between opens, so the next open would resume
   * mid-content. Must run while the dialog has a layout box: a closed dialog is
   * `display: none`, where scrollTop reads as 0 and writes are silently dropped.
   * Vertical only — nested horizontal carousels keep their position.
   * @param {HTMLDialogElement} dialog
   */
  #resetDialogScroll(dialog) {
    if (dialog.scrollTop) dialog.scrollTop = 0;

    for (const el of dialog.querySelectorAll('*')) {
      if (el instanceof HTMLElement && el.scrollTop) el.scrollTop = 0;
    }
  }

  /** Runs for both closeDialog() and native dialog.close(). */
  #onDialogCloseEvent = () => {
    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);
    this.removeAttribute('data-drawer-nested');
    this.refs.dialog.classList.remove('dialog-closing');
    this.#releaseScrollLockAfterClose();
    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Shows the dialog.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) return;

    // Ensure per-dialog lifecycle state is reset before opening.
    dialog.classList.remove('dialog-visible', 'dialog-enter-hold', 'dialog-closing');

    // Nested dialogs (e.g. variant drawer inside quick-add): only the outermost
    // dialog should lock/unlock document.body — otherwise closing the child
    // clears position:fixed and jumps the page under the still-open parent.
    const bodyAlreadyLocked = document.body.style.position === 'fixed';
    this.#ownsScrollLock = !bodyAlreadyLocked;

    if (bodyAlreadyLocked) {
      const lockedTop = Math.abs(parseInt(document.body.style.top || '0', 10));
      this.#previousScrollY = Number.isFinite(lockedTop) ? lockedTop : window.scrollY;
    } else {
      this.#previousScrollY = window.scrollY;
    }

    // Prevent layout thrashing by separating DOM reads from DOM writes
    requestAnimationFrame(() => {
      if (this.#ownsScrollLock) {
        document.body.style.width = '100%';
        document.body.style.position = 'fixed';
        document.body.style.top = `-${this.#previousScrollY}px`;
      }

      // Parks the drawer at its entry position until this frame has rendered — released below.
      dialog.classList.add('dialog-enter-hold');
      dialog.showModal();
      syncNestedDrawerChrome(this, dialog);
      // Before DialogOpenEvent, so listeners that scroll to a target element win.
      this.#resetDialogScroll(dialog);
      // Safety: unlock even if something calls native dialog.close() (variant pick).
      dialog.addEventListener('close', this.#onDialogCloseEvent, { once: true });
      this.dispatchEvent(new DialogOpenEvent());

      // Style, layout and paint of the just-shown subtree land at the end of this frame, so the
      // entry animation is released only once that work is done — otherwise its keyframes are
      // spent against a blocked main thread and the travel is never painted. On a collection
      // page the whole slide used to be lost, which is why drawers appeared to pop into place.
      //
      // The timeout is deliberate, not a stray `setTimeout`: a task queued from inside a frame
      // callback runs *after* that frame has finished rendering, and after any work the paint
      // queued behind it. A second requestAnimationFrame only reaches the *start* of the next
      // frame, which measured both slower to first motion and jerkier.
      //
      // `data-slide-right` no longer selects a slide technique — drawers are back on the
      // shared transform keyframes. It is now only an opt-in for the `.dialog-visible`
      // lifecycle class, which dev-bundle-configurator.js and quick-add.js hook into.
      requestAnimationFrame(() => {
        setTimeout(() => {
          dialog.classList.remove('dialog-enter-hold');

          if (dialog.open && dialog.hasAttribute('data-slide-right')) {
            dialog.classList.add('dialog-visible');
          }
        });
      });

      this.addEventListener('click', this.#handleClick);
      this.addEventListener('keydown', this.#handleKeyDown);
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) return;

    // Second close (double-tap X) must not restart the exit animation. `animation = none`
    // would cancel the running slide-out and getAnimations() would be empty, so this
    // method called dialog.close() in the same turn — the panel vanished. Latch first.
    if (dialog.classList.contains('dialog-closing')) {
      await onAnimationEnd(dialog, undefined, { subtree: false });
      return;
    }

    this.removeEventListener('click', this.#handleClick);
    this.removeEventListener('keydown', this.#handleKeyDown);

    if (dialog.hasAttribute('data-slide-right')) {
      dialog.classList.remove('dialog-visible');
    }

    dialog.classList.add('dialog-closing');

    // Drop the opening animation's forwards fill, then apply the distinct close name.
    dialog.style.animation = 'none';
    void dialog.offsetWidth;
    dialog.style.animation = '';

    // Same scheduling as dialog-enter-hold: getAnimations() in this turn can still see
    // the finished open Animation (or nothing). Then dialog.close() runs and the slide-out
    // never paints. A task queued from rAF runs after that frame has rendered.
    await new Promise((resolve) => {
      requestAnimationFrame(() => {
        setTimeout(resolve);
      });
    });

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    // close event → #onDialogCloseEvent (scroll unlock + DialogCloseEvent)
    dialog.close();
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;
    const target = event.target;

    if (target instanceof Element) {
      const back = target.closest('[data-dev-drawer-back]');
      if (back instanceof HTMLElement && this.contains(back)) {
        event.preventDefault();
        this.closeDialog();
        return;
      }
    }

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses the escape key.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') return;

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number} The minimum width of the dialog.
   */
  get minWidth() {
    return Number(this.getAttribute('dialog-active-min-width'));
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number} The maximum width of the dialog.
   */
  get maxWidth() {
    return Number(this.getAttribute('dialog-active-max-width'));
  }
}

if (!customElements.get('dialog-component')) customElements.define('dialog-component', DialogComponent);

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

document.addEventListener(
  'toggle',
  (event) => {
    if (event.target instanceof HTMLDetailsElement) {
      if (event.target.hasAttribute('scroll-lock')) {
        const { open } = event.target;
        if (open) {
          document.documentElement.setAttribute('scroll-lock', '');
        } else {
          document.documentElement.removeAttribute('scroll-lock');
        }
      }
    }
  },
  { capture: true }
);
